// src/services/deckSubmissionService.js
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

// ─── Request ID Generator ─────────────────────────────────────────────────────
// Format: REQ-20260305-A3K9
// Date prefix makes it human-readable, random suffix ensures uniqueness
const generateRequestId = () => {
  const d = new Date();
  const date = [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `REQ-${date}-${random}`;
};

// ─── Score Extractor ──────────────────────────────────────────────────────────

const extractDecXScore = (htmlContent) => {
  if (!htmlContent) return null;

  const scorePatterns = [
    /<div[^>]*class=["']score["'][^>]*>(\d+)(?:\/100)?<\/div>/i,
    /DecX Score[:\s]+(\d+)(?:\/100)?/i,
    /(\d+)\/100/,
    /score["']?[:\s]+(\d+)/i,
    /data-decx-score=["'](\d+)["']/i,
    /<span[^>]*class=["']decx-score["'][^>]*>(\d+)<\/span>/i,
  ];

  for (const pattern of scorePatterns) {
    const match = htmlContent?.match(pattern);
    if (match?.[1]) {
      const score = parseInt(match[1], 10);
      if (score >= 0 && score <= 100) return score;
    }
  }

  return null;
};

// ─── Company Name Extractor ───────────────────────────────────────────────────

const extractCompanyName = (htmlContent) => {
  if (!htmlContent) return null;

  const patterns = [
    /<h1[^>]*>([^<]+)<\/h1>/i,
    /<div[^>]*class=["']company-name["'][^>]*>([^<]+)<\/div>/i,
    /data-company=["']([^"']+)["']/i,
  ];

  for (const pattern of patterns) {
    const match = htmlContent?.match(pattern);
    if (match?.[1]) return match[1].trim();
  }

  return null;
};

// ─── Service ──────────────────────────────────────────────────────────────────

export const deckSubmissionService = {

  // Create a new deck submission
  async createSubmission(submissionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Ensure user_profiles entry exists
      const { data: existingProfile, error: profileCheckError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email')
        ?.eq('id', user?.id)
        ?.maybeSingle();

      if (!existingProfile && !profileCheckError) {
        const { error: profileCreateError } = await supabase
          ?.from('user_profiles')
          ?.insert([{
            id: user?.id,
            email: user?.email,
            full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User',
            role: 'user',
            created_at: new Date().toISOString(),
          }]);

        if (profileCreateError) {
          console.error('Error creating user profile:', profileCreateError);
          throw new Error('Failed to create user profile. Please try again.');
        }
      }

      const submission = {
        // ✅ Generate request_id here — overrides DB trigger with REQ-20260305-A3K9 format
        request_id: generateRequestId(),

        user_id: user?.id,
        title: submissionData?.title || 'Untitled Submission',
        industry: submissionData?.industry || null,
        audience: submissionData?.audience || null,
        purpose: submissionData?.purpose || null,
        notes: submissionData?.notes || null,
        file_name: submissionData?.fileName,
        file_size: submissionData?.fileSize,
        file_type: submissionData?.fileType,
        file_url: submissionData?.fileUrl,
        slide_count: submissionData?.slideCount || null,
        status: 'pending',
        priority: 'medium',
        submitted_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.insert([submission])
        ?.select()
        ?.single();

      if (error) throw error;

      toast.success('Submission created successfully!');
      return data;
    } catch (error) {
      console.error('Error creating submission:', error);
      toast.error('Failed to create submission: ' + error.message);
      throw error;
    }
  },

  // Get all submissions for current user
  async getUserSubmissions(userId) {
    try {
      if (!userId) throw new Error('User ID is required');

      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.select(`
          *,
          audit_reports (
            id,
            status,
            published_at,
            html_content,
            report_id
          )
        `)
        ?.eq('user_id', userId)
        ?.order('submitted_at', { ascending: false });

      if (error) throw error;

      const mappedData = data?.map(submission => {
        if (submission?.audit_reports?.length > 0) {
          const auditReport = submission.audit_reports[0];
          return {
            ...submission,
            status: auditReport?.status || submission?.status,
            published_at: auditReport?.published_at,
            decx_score: extractDecXScore(auditReport?.html_content),
            company_name: extractCompanyName(auditReport?.html_content),
            report_id: auditReport?.report_id,
          };
        }
        return submission;
      }) || [];

      return mappedData;
    } catch (error) {
      console.error('Error fetching user submissions:', error);
      throw error;
    }
  },

  // Get a single submission by ID
  async getSubmissionById(submissionId) {
    try {
      if (!submissionId) throw new Error('Submission ID is required');

      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.select(`
          *,
          user_profiles!deck_submissions_user_id_fkey (
            email,
            full_name
          ),
          audit_reports (
            id,
            status,
            published_at,
            html_content,
            report_id,
            html_url
          )
        `)
        ?.eq('id', submissionId)
        ?.single();

      if (error) throw error;

      if (data?.audit_reports?.length > 0) {
        const auditReport = data.audit_reports[0];
        data.decx_score = extractDecXScore(auditReport?.html_content);
        data.company_name = extractCompanyName(auditReport?.html_content);
        data.report_id = auditReport?.report_id;
        data.html_url = auditReport?.html_url;
      }

      return data;
    } catch (error) {
      console.error('Error fetching submission:', error);
      throw error;
    }
  },

  // Get all submissions (admin only)
  async getAllSubmissions(filters = {}) {
    try {
      let query = supabase
        ?.from('deck_submissions')
        ?.select(`
          *,
          user_profiles!deck_submissions_user_id_fkey (
            email,
            full_name
          )
        `);

      if (filters.status)    query = query?.eq('status', filters.status);
      if (filters.priority)  query = query?.eq('priority', filters.priority);
      if (filters.userId)    query = query?.eq('user_id', filters.userId);
      if (filters.search)    query = query?.or(`title.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`);
      if (filters.startDate) query = query?.gte('submitted_at', filters.startDate);
      if (filters.endDate)   query = query?.lte('submitted_at', filters.endDate);

      const { data, error } = await query?.order('submitted_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching all submissions:', error);
      throw error;
    }
  },

  // Update submission status
  async updateSubmissionStatus(submissionId, status, notes = null) {
    try {
      const updates = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'in_review') updates.reviewed_at = new Date().toISOString();
      if (status === 'completed') updates.completed_at = new Date().toISOString();
      if (notes) updates.admin_notes = notes;

      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.update(updates)
        ?.eq('id', submissionId)
        ?.select()
        ?.single();

      if (error) throw error;

      const statusMessages = {
        pending:   'Submission status changed to Pending',
        in_review: 'Submission is now In Review',
        completed: 'Submission completed successfully',
      };

      if (statusMessages[status]) {
        toast.success(statusMessages[status], { icon: status === 'completed' ? '✅' : '🔄' });
      }

      return data;
    } catch (error) {
      console.error('Error updating submission status:', error);
      toast.error('Failed to update submission status');
      throw error;
    }
  },

  // Update submission priority
  async updateSubmissionPriority(submissionId, priority) {
    try {
      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.update({ priority, updated_at: new Date().toISOString() })
        ?.eq('id', submissionId)
        ?.select()
        ?.single();

      if (error) throw error;

      toast.success(`Priority updated to ${priority}`);
      return data;
    } catch (error) {
      console.error('Error updating submission priority:', error);
      toast.error('Failed to update priority');
      throw error;
    }
  },

  // Assign analyst to submission
  async assignAnalyst(submissionId, analystId, analystName) {
    try {
      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.update({
          assigned_analyst_id: analystId,
          assigned_analyst: analystName,
          assigned_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        ?.eq('id', submissionId)
        ?.select()
        ?.single();

      if (error) throw error;

      toast.success(`Assigned to ${analystName}`);
      return data;
    } catch (error) {
      console.error('Error assigning analyst:', error);
      toast.error('Failed to assign analyst');
      throw error;
    }
  },

  // Get submission statistics
  async getSubmissionStats(timeframe = 'all') {
    try {
      let query = supabase?.from('deck_submissions')?.select('status, priority, submitted_at');

      if (timeframe !== 'all') {
        const date = new Date();
        if (timeframe === 'today')  { date.setHours(0, 0, 0, 0); query = query?.gte('submitted_at', date.toISOString()); }
        if (timeframe === 'week')   { date.setDate(date.getDate() - 7); query = query?.gte('submitted_at', date.toISOString()); }
        if (timeframe === 'month')  { date.setMonth(date.getMonth() - 1); query = query?.gte('submitted_at', date.toISOString()); }
      }

      const { data, error } = await query;
      if (error) throw error;

      return {
        total:          data?.length || 0,
        pending:        data?.filter(s => s?.status === 'pending')?.length || 0,
        inReview:       data?.filter(s => s?.status === 'in_review')?.length || 0,
        completed:      data?.filter(s => s?.status === 'completed')?.length || 0,
        highPriority:   data?.filter(s => s?.priority === 'high')?.length || 0,
        mediumPriority: data?.filter(s => s?.priority === 'medium')?.length || 0,
        lowPriority:    data?.filter(s => s?.priority === 'low')?.length || 0,
      };
    } catch (error) {
      console.error('Error fetching submission stats:', error);
      throw error;
    }
  },

  // Mark submission as completed when audit is published
  async markSubmissionCompleted(submissionId, decxScore = null, reportId = null) {
    try {
      const updates = {
        status: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (decxScore !== null && decxScore !== undefined) updates.decx_score = decxScore;
      if (reportId) updates.report_id = reportId;

      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.update(updates)
        ?.eq('id', submissionId)
        ?.select();

      if (error) throw error;

      if (!data || data?.length === 0) {
        console.warn(`No submission found with id: ${submissionId}`);
        return null;
      }

      toast.success('Submission marked as completed!');
      return data[0];
    } catch (error) {
      console.error('Error marking submission as completed:', error);
      toast.error('Failed to mark submission as completed');
      throw error;
    }
  },

  // Extract and update DecX score for a submission
  async updateDecXScore(submissionId, htmlContent) {
    try {
      const updates = {};
      const decxScore = extractDecXScore(htmlContent);
      const companyName = extractCompanyName(htmlContent);

      if (decxScore !== null) updates.decx_score = decxScore;
      if (companyName) updates.company_name = companyName;

      if (Object.keys(updates).length > 0) {
        updates.updated_at = new Date().toISOString();

        const { data, error } = await supabase
          ?.from('deck_submissions')
          ?.update(updates)
          ?.eq('id', submissionId)
          ?.select()
          ?.single();

        if (error) throw error;
        return data;
      }

      return null;
    } catch (error) {
      console.error('Error updating DecX score:', error);
      throw error;
    }
  },

  // Delete submission
  async deleteSubmission(submissionId) {
    try {
      if (!submissionId) throw new Error('Submission ID is required');

      const { error } = await supabase
        ?.from('deck_submissions')
        ?.delete()
        ?.eq('id', submissionId);

      if (error) throw error;

      toast.success('Submission deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
      throw error;
    }
  },

  // Bulk update submissions
  async bulkUpdateSubmissions(submissionIds, updates) {
    try {
      if (!submissionIds?.length) throw new Error('No submissions selected');

      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.update({ ...updates, updated_at: new Date().toISOString() })
        ?.in('id', submissionIds);

      if (error) throw error;

      toast.success(`Updated ${submissionIds.length} submissions`);
      return data;
    } catch (error) {
      console.error('Error bulk updating submissions:', error);
      toast.error('Failed to update submissions');
      throw error;
    }
  },
};

export default deckSubmissionService;